<template>
  <div :class="[containerClasses, outerClass, disabledClasses]" :data-test-id="dataTestId" :style="inlineStyle">
    <PanelTitle
      v-if="(fields?.heading.value ?? heading.value) || (fields?.headingDescription.value ?? headingDescription.value)"
      :title="fields?.heading ?? heading"
      :subTitle="fields?.headingDescription ?? headingDescription"
      :isTextBlock="{ value: true }"
      :bindings="{ outerClass: 'mb-5' }"
    />

    <PanelTitle
      v-if="
        (fields?.subheading.value ?? subheading.value) ||
        (fields?.subheadingDescription.value ?? subheadingDescription.value)
      "
      :title="fields?.subheading ?? subheading"
      :subTitle="fields?.subheadingDescription ?? subheadingDescription"
      :panelType="{ value: PanelTitleTypes.Subheading }"
      :bindings="{ outerClass: 'mb-2' }"
    />

    <div :class="wrapperClasses">
      <Link
        v-for="(item, index) in displayedLinks"
        :key="index"
        :disabled="disabledPanel ? true : undefined"
        :class="{
          'opacity-50 cursor-not-allowed':
            (((item.fields?.link ?? item.link).value.href?.length ?? 0) === 0 || disabledPanel) &&
            !handleLinkClickEvent,
        }"
        :field="getLinkField(item)"
        :data-test-id="item.fields?.dataTestId?.value ?? item?.dataTestId?.value ?? item.name"
        class="group block hover:bg-racq-anti-flash-white active:bg-racq-white no-underline active:no-underline"
        @click="onClick(item)"
      >
        <div
          class="flex items-center py-5 mx-5 md:mx-8 border-racq-white"
          :class="{
            'items-center': isSize32Icons,
            'items-start': !isSize32Icons,
            'border-b': index < displayedLinks.length - 1,
            'border-0': index == displayedLinks.length - 1,
          }"
        >
          <div
            v-if="(fields?.globalIcon ?? globalIcon)?.value?.src || (item.fields?.icon ?? item.icon)?.value?.src"
            class="flex mr-4 flex-shrink-0 items-center justify-center"
            :class="iconSizeClasses"
          >
            <Image
              :media="
                ((fields?.globalIcon && !isEmptyObject(fields?.globalIcon)) ?? globalIcon)?.value?.src
                  ? globalIcon
                  : (item.fields?.icon ?? item.icon)
              "
              class="w-full h-auto"
            />
          </div>

          <div :class="contentGridClasses" class="w-full">
            <div :class="textBlockClasses">
              <h4 v-if="item.fields?.title?.value || item.title?.value" class="text-racq-dark-grey text-base-bold">
                <RichText :field="item.fields?.title ?? item.title" />
              </h4>
              <div class="text-racq-med-grey text-m richtext">
                <RichText :field="item.fields?.subTitle ?? item.subTitle" />
              </div>
            </div>

            <div :class="actionClasses">
              <template v-if="isArrowLinks">
                <BaseIcon iconClass="bg-icon-chevron" class="!w-4 !h-4" />
              </template>
              <template v-else>
                <Text
                  :field="
                    globalButtonLabel?.value && globalButtonLabel.value.trim() !== ''
                      ? globalButtonLabel
                      : item.buttonLabel
                  "
                  class="underline text-base-bold group-hover:no-underline group-active:no-underline decoration-[10%]"
                />
              </template>
            </div>
          </div>
        </div>
      </Link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-vue';
import { Text, RichText, Image, Link } from '@sitecore-jss/sitecore-jss-vue';
import { ImageField } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';

import { LinkPanelItem } from '@/interfaces/components/link-panel-item.interface';
import { PanelTitleTypes } from '@/enums/panel-title-type.enum';

import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import BaseIcon from '@/components/BaseIcon/BaseIcon.vue';
import { LinkPanelIconSizes } from '@/enums/link-panel-enum';
import { ComponentFields } from '@sitecore-jss/sitecore-jss-vue';
import { usePageStore } from '@/store';
import { isEmptyObject } from 'jquery';
import { useRoute } from 'vue-router';
import { replaceBookmarksFromRoute } from '@/utils/bookmark.replace';

const props = withDefaults(
  defineProps<{
    bindings?: Record<string, any>;
    heading?: Field<string>;
    headingDescription?: Field<string>;
    subheading?: Field<string>;
    subheadingDescription?: Field<string>;
    links: LinkPanelItem[];
    isGreyBackground?: Field<boolean>;
    isArrowLinks: Field<boolean>;
    iconSize?: Field<string>;
    globalIcon: ImageField;
    globalButtonLabel?: Field<string>;
    fields?: ComponentFields;
    rendering?: ComponentRendering;
    handleLinkClickEvent?: boolean;
    disabledPanel?: boolean;
  }>(),
  {
    handleLinkClickEvent: false,
  }
);
const outerClass = props.rendering?.params?.Styles;
const inlineStyle = props.rendering?.params?.inlineStyle;
const route = useRoute();
const dataTestId = usePageStore().dataTestId(props.rendering);
const maxSupportedLinkItems = 6;
const isGrey = computed(() => (props.fields?.isGreyBackground ?? props.isGreyBackground)?.value ?? false);
const isArrowLinks = computed(() => (props.fields?.isArrowLinks ?? props.isArrowLinks).value);

const containerClasses = computed(() => ['max-w-176 w-full', (props.fields?.bindings ?? props.bindings)?.outerClass]);

const disabledClasses = computed(() => ['disabled:opacity-75  cursor-not-allowed', props.disabledPanel]);

const wrapperClasses = computed(() => [
  'border-[1.5px] border-solid rounded-lg shadow-md',
  isGrey.value ? 'bg-racq-neutral-grey' : 'py-2 bg-white',
]);

const contentGridClasses = computed(() => [
  isArrowLinks.value ? 'flex' : 'grid grid-cols-1 gap-1 md:grid-cols-5 md:gap-4',
]);

const textBlockClasses = computed(() => [isArrowLinks.value ? 'flex-grow' : 'col-span-2 md:col-span-4']);

const actionClasses = computed(() => [
  isArrowLinks.value ? 'ml-8 flex flex-col justify-center' : 'text-left md:text-right flex flex-col justify-center',
]);

const displayedLinks = computed(() => {
  const links = (props.fields?.items ?? props.links).slice(0, maxSupportedLinkItems);
  return links.map((link: any) => {
    const linkItem = link.fields?.link /** SC rendering */ ?? link.link; /** Page builder */
    // To replace querystring for links like cancel insurance /cancel roadside
    if (linkItem?.value?.querystring)
      linkItem.value.querystring = replaceBookmarksFromRoute(linkItem.value.querystring, route);
    return link;
  });
});

const isSize32Icons = computed(() => (props.fields?.iconSize ?? props.iconSize)?.value === LinkPanelIconSizes.Size32);
const iconSizeClasses = computed(() => (isSize32Icons.value ? 'w-[32px] h-[32px]' : 'w-[24px] h-[24px]'));

const emit = defineEmits(['btnClick']);

const onClick = (linkItem: any) => {
  if (!props.handleLinkClickEvent) return;
  emit('btnClick', linkItem);
};

const getLinkField = (item: any) => {
  const originalLink = item.fields?.link ?? item.link;

  if (props.handleLinkClickEvent === true && originalLink?.value) {
    return {
      value: {
        ...originalLink.value,
        href: 'javascript:void(0)',
      },
    };
  }

  return originalLink;
};
</script>
